// src/feature-module/admin/shadowing/ReviewShadowingSubmissionPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaClipboardCheck,
  FaSpinner,
  FaCheckCircle,
  FaPlay,
  FaPause,
  FaMicrophone,
  FaUser,
  FaCalendarAlt,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Submission = {
  id: number;
  studentId: number;
  studentName: string;
  taskNumber: number;
  taskDescription: string;
  level: string;
  audioDownloadUrl: string;
  originalAudioUrl: string;
  submittedAt: string;
  reviewed: boolean;
  mentorFeedback: string;
  reviewedBy: string;
  reviewedAt: string;
};

const ReviewShadowingSubmissionPage: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState<Submission | null>(null);
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Audio playback states
  const [isPlayingStudent, setIsPlayingStudent] = useState(false);
  const [isPlayingOriginal, setIsPlayingOriginal] = useState(false);

  const studentAudioRef = React.useRef<HTMLAudioElement>(null);
  const originalAudioRef = React.useRef<HTMLAudioElement>(null);

  useEffect(() => {
    fetchSubmission();
  }, [submissionId]);

  const fetchSubmission = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks/submissions/${submissionId}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setSubmission(data);
        setFeedback(data.mentorFeedback || "");
      } else {
        setError("Failed to load submission details");
      }
    } catch (err) {
      console.error("Error fetching submission:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks/submissions/${submissionId}/review`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mentorFeedback: feedback.trim() || null }),
        }
      );

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate(-1); // Go back to previous page
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || "Failed to submit review");
      }
    } catch (err) {
      console.error("Error submitting review:", err);
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStudentAudio = () => {
    if (studentAudioRef.current) {
      if (isPlayingStudent) {
        studentAudioRef.current.pause();
      } else {
        studentAudioRef.current.play();
      }
    }
  };

  const toggleOriginalAudio = () => {
    if (originalAudioRef.current) {
      if (isPlayingOriginal) {
        originalAudioRef.current.pause();
      } else {
        originalAudioRef.current.play();
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">Submission not found</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-cyan-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaClipboardCheck />}
        title="Review Shadowing Submission"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Shadowing Tasks", to: "/admin/shadowing-tasks" },
          { label: "Review Submission" },
        ]}
      />

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <FaCheckCircle className="text-green-600 text-xl" />
            <div>
              <h3 className="font-semibold text-green-800">
                Review Submitted!
              </h3>
              <p className="text-sm text-green-700">Redirecting...</p>
            </div>
          </div>
        )}

        {/* Submission Info Card */}
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Submission Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <FaUser className="text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Student</p>
                <p className="font-semibold text-gray-900">
                  {submission.studentName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FaCalendarAlt className="text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Submitted</p>
                <p className="font-semibold text-gray-900">
                  {new Date(submission.submittedAt).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FaMicrophone className="text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Task</p>
                <p className="font-semibold text-gray-900">
                  {submission.level} - Task #{submission.taskNumber}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FaCheckCircle
                className={
                  submission.reviewed ? "text-green-600" : "text-yellow-600"
                }
              />
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <p className="font-semibold text-gray-900">
                  {submission.reviewed ? "Reviewed" : "Pending Review"}
                </p>
              </div>
            </div>
          </div>
          {submission.taskDescription && (
            <div className="mt-4 pt-4 border-t border-blue-200">
              <p className="text-xs text-gray-500 mb-1">Task Description</p>
              <p className="text-gray-700">{submission.taskDescription}</p>
            </div>
          )}
        </div>

        {/* Audio Players */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Original Audio */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaMicrophone className="text-blue-600" />
              Original Task Audio
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                Listen to the original task audio:
              </p>
              <audio
                ref={originalAudioRef}
                controls
                className="w-full"
                src={submission.originalAudioUrl}
                onPlay={() => setIsPlayingOriginal(true)}
                onPause={() => setIsPlayingOriginal(false)}
                onEnded={() => setIsPlayingOriginal(false)}
              >
                Your browser does not support audio playback.
              </audio>
            </div>
            <button
              onClick={toggleOriginalAudio}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              {isPlayingOriginal ? (
                <>
                  <FaPause />
                  Pause
                </>
              ) : (
                <>
                  <FaPlay />
                  Play Original
                </>
              )}
            </button>
          </div>

          {/* Student's Recording */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaUser className="text-green-600" />
              Student's Recording
            </h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                Listen to the student's submission:
              </p>
              <audio
                ref={studentAudioRef}
                controls
                className="w-full"
                src={submission.audioDownloadUrl}
                onPlay={() => setIsPlayingStudent(true)}
                onPause={() => setIsPlayingStudent(false)}
                onEnded={() => setIsPlayingStudent(false)}
              >
                Your browser does not support audio playback.
              </audio>
            </div>
            <button
              onClick={toggleStudentAudio}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              {isPlayingStudent ? (
                <>
                  <FaPause />
                  Pause
                </>
              ) : (
                <>
                  <FaPlay />
                  Play Student
                </>
              )}
            </button>
          </div>
        </div>

        {/* Previous Review (if exists) */}
        {submission.reviewed && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-yellow-900 mb-2">
              Previously Reviewed
            </h3>
            <p className="text-sm text-yellow-700 mb-2">
              Reviewed by: <strong>{submission.reviewedBy}</strong> on{" "}
              {new Date(submission.reviewedAt).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            {submission.mentorFeedback && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-yellow-300">
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Previous Feedback:
                </p>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {submission.mentorFeedback}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Review Form */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaClipboardCheck className="text-[#009fe3]" />
            {submission.reviewed ? "Update Review" : "Submit Review"}
          </h3>

          <form onSubmit={handleSubmitReview} className="space-y-6">
            {/* Feedback Text Area */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Feedback (Optional)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Provide constructive feedback on pronunciation, fluency, or areas for improvement..."
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all resize-none"
                style={{ fontSize: "16px" }}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank if no specific feedback is needed.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={submitting || success}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    {submission.reviewed ? "Update Review" : "Mark as Reviewed"}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={submitting}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ReviewShadowingSubmissionPage;
